import { Table, Button } from 'react-bootstrap';
import { useGetStocksQuery } from '../../slices/stocksApiSlice';
import { useGetSuppliesQuery } from '../../slices/suppliesApiSlice';
import { useGetTransfersQuery } from '../../slices/transfersApiSlice';
import { useGetExpensesQuery } from '../../slices/expensesApiSlice';
import { useGetIncomesQuery } from '../../slices/incomesApiSlice';

import { useGetStoreStocksQuery } from '../../slices/storeStocksApiSlice';
import { useGetStoreSuppliesQuery } from '../../slices/storeSuppliesApiSlice';
import { useGetStoreTransfersQuery } from '../../slices/storeTransfersApiSlice';
import { useGetStoreExpensesQuery } from '../../slices/storeExpensesApiSlice';
import { useGetStoreIncomesQuery } from '../../slices/storeIncomesApiSlice';

import Message from '../../components/Message';
import Loader from '../../components/Loader';
import { useState, useMemo, useCallback } from 'react';
import * as XLSX from 'xlsx';

const ConsolidatedWorkingCapital = () => {
  // MAIN DATA
  const { data: stocksData, isLoading, error } = useGetStocksQuery();
  const { data: suppliesData } = useGetSuppliesQuery();
  const { data: transfersData } = useGetTransfersQuery();

  // MAIN ARRAYS
  const stocks = useMemo(() => stocksData?.stocks || [], [stocksData]);
  const supplies = useMemo(() => suppliesData?.supplies || [], [suppliesData]);
  const transfers = useMemo(() => transfersData?.transfers || [], [transfersData]);

  // Extract unique dates
  const uniqueDates = useMemo(() => {
    return stocks.length > 0
      ? [...new Set(stocks.map(s => new Date(s.date).toISOString().split('T')[0]))]
      : [];
  }, [stocks]);

  const sortedDates = useMemo(
    () => uniqueDates.sort((a, b) => new Date(a) - new Date(b)),
    [uniqueDates]
  );

  const date1 = sortedDates;
  const date2 = [...sortedDates.slice(1), sortedDates[sortedDates.length - 1]];

  // MAIN date-filtered queries
  const startDate = date1[0];
  const endDate = date2[date2.length - 1];

  const { data: expensesData } = useGetExpensesQuery(
    startDate && endDate ? { startDate, endDate } : undefined
  );

  const { data: incomesData } = useGetIncomesQuery(
    startDate && endDate ? { startDate, endDate } : undefined
  );

  // STORE DATA
  const { data: storeStocksData } = useGetStoreStocksQuery();
  const { data: storeSuppliesData } = useGetStoreSuppliesQuery();
  const { data: storeTransfersData } = useGetStoreTransfersQuery();

  const { data: storeExpensesData } = useGetStoreExpensesQuery(
    startDate && endDate ? { startDate, endDate } : undefined
  );

  const { data: storeIncomesData } = useGetStoreIncomesQuery(
    startDate && endDate ? { startDate, endDate } : undefined
  );

  // STORE ARRAYS
  const storeStocks = useMemo(() => storeStocksData?.storeStocks || [], [storeStocksData]);
  const storeSupplies = useMemo(() => storeSuppliesData?.storeSupplies || [], [storeSuppliesData]);
  const storeTransfers = useMemo(() => storeTransfersData?.storeTransfers || [], [storeTransfersData]);
  const storeExpenses = useMemo(() => storeExpensesData?.storeExpenses || [], [storeExpensesData]);
  const storeIncomes = useMemo(() => storeIncomesData?.storeIncomes || [], [storeIncomesData]);

  const expenses = useMemo(() => expensesData?.expenses || [], [expensesData]);
  const incomes = useMemo(() => incomesData?.incomes || [], [incomesData]);

  const [consolidatedValues, setConsolidatedValues] = useState({});

  // -----------------------------
  // MAIN CALCULATION FUNCTIONS
  // -----------------------------

  const calcStockValue = useCallback(
    date => {
      return stocks
        .filter(s => new Date(s.date).toISOString().split('T')[0] === date)
        .flatMap(s => s.stockItems || [])
        .reduce((acc, item) => acc + item.price * item.qty, 0);
    },
    [stocks]
  );

  const calcSupplyValue = useCallback(
    (start, cutoff) => {
      return supplies
        .filter(s => {
          const d = new Date(s.date).toISOString().split('T')[0];
          return d >= start && d <= cutoff;
        })
        .flatMap(s => s.supplyItems || [])
        .reduce((acc, item) => acc + item.price * item.qty, 0);
    },
    [supplies]
  );

  const calcTransferValue = useCallback(
    (start, cutoff) => {
      return transfers
        .filter(t => {
          const d = new Date(t.date).toISOString().split('T')[0];
          return d >= start && d <= cutoff;
        })
        .flatMap(t => t.transferItems || [])
        .reduce((acc, item) => acc + item.price * item.qty, 0);
    },
    [transfers]
  );

  const calcExpenseValue = useCallback(
    (start, cutoff) => {
      return expenses
        .filter(e => {
          const d = new Date(e.date).getTime();
          return d >= new Date(start).getTime() && d <= new Date(cutoff).getTime();
        })
        .reduce((acc, e) => acc + Number(e.amount), 0);
    },
    [expenses]
  );

  const calcIncomeValue = useCallback(
    (start, cutoff) => {
      return incomes
        .filter(i => {
          const d = new Date(i.date).getTime();
          return d >= new Date(start).getTime() && d <= new Date(cutoff).getTime();
        })
        .reduce((acc, i) => acc + Number(i.amount), 0);
    },
    [incomes]
  );

  // MAIN credit/payment with cutoff
  const calcSalesCredit = useCallback(
    (start, cutoff) => {
      return expenses
        .filter(e => {
          const d = new Date(e.date).toISOString().split('T')[0];
          return e.category === 'SalesCredit' && d >= start && d <= cutoff;
        })
        .reduce((acc, e) => acc + Number(e.amount), 0);
    },
    [expenses]
  );

  const calcSalesPayment = useCallback(
    (start, cutoff) => {
      return expenses
        .filter(e => {
          const d = new Date(e.date).toISOString().split('T')[0];
          return e.category === 'SalesPayment' && d >= start && d <= cutoff;
        })
        .reduce((acc, e) => acc + Number(e.amount), 0);
    },
    [expenses]
  );

  const calcCumulativeMainReceivable = useCallback(
    (start, cutoff) => {
      return expenses
        .filter(e => {
          const d = new Date(e.date).toISOString().split("T")[0];
          return (
            d >= start &&
            d <= cutoff &&
            (e.category === "SalesCredit" || e.category === "SalesPayment")
          );
        })
        .reduce((sum, e) => sum + Number(e.amount), 0);
    },
    [expenses]
  );

  // -----------------------------
  // STORE CALCULATION FUNCTIONS
  // -----------------------------

  const calcStoreStockValue = useCallback(
    date => {
      return storeStocks
        .filter(s => new Date(s.date).toISOString().split('T')[0] === date)
        .flatMap(s => s.storeStockItems || [])
        .reduce((acc, item) => acc + item.price * item.qty, 0);
    },
    [storeStocks]
  );

  const calcStoreSupplyValue = useCallback(
    (start, cutoff) => {
      return storeSupplies
        .filter(s => {
          const d = new Date(s.date).toISOString().split('T')[0];
          return d >= start && d <= cutoff;
        })
        .flatMap(s => s.storeSupplyItems || [])
        .reduce((acc, item) => acc + item.price * item.qty, 0);
    },
    [storeSupplies]
  );

  const calcStoreTransferValue = useCallback(
    (start, cutoff) => {
      return storeTransfers
        .filter(t => {
          const d = new Date(t.date).toISOString().split('T')[0];
          return d >= start && d <= cutoff;
        })
        .flatMap(t => t.storeTransferItems || [])
        .reduce((acc, item) => acc + item.price * item.qty, 0);
    },
    [storeTransfers]
  );

  const calcStoreExpenseValue = useCallback(
    (start, cutoff) => {
      return storeExpenses
        .filter(e => {
          const d = new Date(e.date).getTime();
          return d >= new Date(start).getTime() && d <= new Date(cutoff).getTime();
        })
        .reduce((acc, e) => acc + Number(e.amount), 0);
    },
    [storeExpenses]
  );

  const calcStoreIncomeValue = useCallback(
    (start, cutoff) => {
      return storeIncomes
        .filter(i => {
          const d = new Date(i.date).getTime();
          return d >= new Date(start).getTime() && d <= new Date(cutoff).getTime();
        })
        .reduce((acc, i) => acc + Number(i.amount), 0);
    },
    [storeIncomes]
  );

  const calcStoreSalesCredit = useCallback(
    (start, cutoff) => {
      return storeExpenses
        .filter(e => {
          const d = new Date(e.date).toISOString().split('T')[0];
          return e.category === 'StoreSalesCredit' && d >= start && d <= cutoff;
        })
        .reduce((acc, e) => acc + Number(e.amount), 0);
    },
    [storeExpenses]
  );

  const calcStoreSalesPayment = useCallback(
    (start, cutoff) => {
      return storeExpenses
        .filter(e => {
          const d = new Date(e.date).toISOString().split('T')[0];
          return e.category === 'StoreSalesPayment' && d >= start && d <= cutoff;
        })
        .reduce((acc, e) => acc + Number(e.amount), 0);
    },
    [storeExpenses]
  );

  const calcCumulativeStoreReceivable = useCallback(
    (start, cutoff) => {
      return storeExpenses
        .filter(e => {
          const d = new Date(e.date).toISOString().split("T")[0];
          return (
            d >= start &&
            d <= cutoff &&
            (e.category === "StoreSalesCredit" || e.category === "StoreSalesPayment")
          );
        })
        .reduce((sum, e) => sum + Number(e.amount), 0);
    },
    [storeExpenses]
  );

  // -----------------------------
  // CONSOLIDATION LOGIC
  // -----------------------------

  const recalc = () => {
    const updated = date1.reduce((acc, date, index) => {
      const start = date;
      const end = date2[index];

      // NEW: cutoff date = end - 1 day
      const cutoff = new Date(end);
      cutoff.setDate(cutoff.getDate() - 1);
      const cutoffDate = cutoff.toISOString().split("T")[0];

      // MAIN
      const opening = calcStockValue(start);
      const supply = calcSupplyValue(start, cutoffDate);
      const transfer = calcTransferValue(start, cutoffDate);
      const expense = calcExpenseValue(start, cutoffDate);
      const income = calcIncomeValue(start, cutoffDate);
      const credit = calcSalesCredit(start, cutoffDate);
      const payment = calcSalesPayment(start, cutoffDate);
      const closing = calcStockValue(end);

      const net = opening + supply + expense - transfer - closing;
      const varValue = income - net;

      const cumulativeMain = calcCumulativeMainReceivable(date1[0], cutoffDate);

      // STORE
      const sOpening = calcStoreStockValue(start);
      const sSupply = calcStoreSupplyValue(start, cutoffDate);
      const sTransfer = calcStoreTransferValue(start, cutoffDate);
      const sExpense = calcStoreExpenseValue(start, cutoffDate);
      const sIncome = calcStoreIncomeValue(start, cutoffDate);
      const sCredit = calcStoreSalesCredit(start, cutoffDate);
      const sPayment = calcStoreSalesPayment(start, cutoffDate);
      const sClosing = calcStoreStockValue(end);

      const sNet = sOpening + sSupply + sExpense - sTransfer - sClosing;
      const sVar = sIncome - sNet;

      const cumulativeStore = calcCumulativeStoreReceivable(date1[0], cutoffDate);

      // CONSOLIDATED
      acc[date] = {
        opening: opening + sOpening,
        supply: supply + sSupply,
        transfer: transfer + sTransfer,
        expense: expense + sExpense,
        income: income + sIncome,
        credit: credit + sCredit,
        payment: payment + sPayment,
        receivableInc: (credit + payment) + (sCredit + sPayment),
        closing: closing + sClosing,
        net: net + sNet,
        varValue: varValue + sVar,
        rvar: cumulativeMain + cumulativeStore,
      };

      return acc;
    }, {});

    setConsolidatedValues(updated);
  };

  if (isLoading) return <Loader />;
  if (error)
    return (
      <Message variant="danger">
        {error?.data?.message || 'Error fetching data'}
      </Message>
    );
  if (stocks.length === 0)
    return <Message variant="warning">No stock data available</Message>;

  const exportToExcel = () => {
    const tableData = date1.map((date, index) => {
      const v = consolidatedValues[date] || {};
      return {
        Date1: date,
        Date2: date2[index],
        'Opening Stock Value': v.opening,
        'Supply Value': v.supply,
        'Transfer Value': v.transfer,
        'Expense Value': v.expense,
        'Closing Stock Value': v.closing,
        'Net Value': v.net,
        'Income Value': v.income,
        Var: v.varValue,
        Credit: v.credit,
        Payment: v.payment,
        'Receivable Inc': v.receivableInc,
        RVar: v.rvar,
      };
    });

    const ws = XLSX.utils.json_to_sheet(tableData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Consolidated Working Capital');
    XLSX.writeFile(wb, 'ConsolidatedWorkingCapital.xlsx');
  };

  return (
    <div>
      <h1>Consolidated Working Capital</h1>

      <Button onClick={recalc}>Recalculate</Button>

      <Table striped bordered hover>
        <thead>
          <tr>
            <th>Date1</th>
            <th>Date2</th>
            <th>Opening</th>
            <th>Supply</th>
            <th>Transfer</th>
            <th>Expense</th>
            <th>Closing</th>
            <th>Net</th>
            <th>Income</th>
            <th>Var</th>
            <th>Credit</th>
            <th>Payment</th>
            <th>Receivable Inc</th>
            <th>RVar</th>
          </tr>
        </thead>

        <tbody>
          {date1.map((date, index) => {
            const v = consolidatedValues[date] || {};
            return (
              <tr key={date}>
                <td>{date}</td>
                <td>{date2[index]}</td>
                <td>{v.opening}</td>
                <td>{v.supply}</td>
                <td>{v.transfer}</td>
                <td>{v.expense}</td>
                <td>{v.closing}</td>
                <td>{v.net}</td>
                <td>{v.income}</td>
                <td>{v.varValue}</td>
                <td>{v.credit}</td>
                <td>{v.payment}</td>
                <td>{v.receivableInc}</td>
                <td>{v.rvar}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      <Button onClick={exportToExcel}>Export to Excel</Button>
    </div>
  );
};

export default ConsolidatedWorkingCapital;
